/*
 * @CopyRight    : 广州仰望星空云科技有限公司
 * @Author       : Kyle
 * @Email        : linyukai@xinyu668.com
 * @CreateTime   : 2023-08-15 14:49:03
 * @Description  : 错误监控
 */
// =================================================================
import { bridgeClient } from '@UTILS/bridge';
import { client } from '@UTILS/client'
import { constConf } from '@GLOBAL/constConf'
import { jsonStringify } from '@ywxk/util/format'
import { isType } from '@ywxk/util/validate'
// import 'virtual:vite-plugin-sentry/sentry-config'
import { init, BrowserTracing, vueRouterInstrumentation, Replay } from '@sentry/vue'

const { IS_PROD, IS_LOCAL, SENTRY_COMMONALITY_DSN, PROJECT, FOLDER, IS_APP_LOCAL, ORIGIN_PATHNAME, IS_FORMAT, IS_DEBUG } = constConf

// sentry只能看到浅层对象,需要序列化输出
if (IS_PROD && !IS_LOCAL && !IS_DEBUG && !IS_FORMAT) { // 留一个出口，可以看不序列化的内容方便调试
  const _log = window.console.log
  window.console.log = (...rest) => {
    const args = Array.from(rest).map((item) => (isType(['Array', 'Object'], item) ? jsonStringify(item) : item))
    _log(...args)
  }
}

// 占位
if (!(window as any).Sentry) {
  ; (window as any).Sentry = {}
    ;['captureMessage', 'captureException', 'captureEvent'].forEach((name) => ((window as any).Sentry[name] = function () { }))
}

// 未捕获错误
const KEYWORD = [
  /账号信息获取失败，请重新登录/,
  /网络连接已中断/,
  /请求太频繁，请稍后重试/,
  /暂不支持未成年人账号充值/,
  /当前账号已被触发，暂不能充值/,
  /您所充值的账号已注销，无法进行充值/,
  /Network Error/,
  /insert error/,
  /timeout exceeded/,
  /The operation was aborted/,
  /The operation is insecure/,
  // db报错问题，不影响渲染
  /Failed to execute 'transaction' on 'IDBDatabase'/,
  /timeout of (10000|30000)ms exceeded/,

  // 怀疑是oxui的svg报错
  /null is not an object \(evaluating 'e.insertBefore'\)/,
  /Cannot read property 'insertBefore' of null/,
  // apng-js报错
  /Failed to fetch/,
  /未能完成操作。（kCFErrorDomainCFNetwork错误303。）/,
  /Load failed/,
  /Image creation error/,
  /已取消/,
  // svga第三方库报错
  /c35 is not defined/,
  /video item undefined/,
  // IntersectionObserver兼容问题
  /Can't find variable: IntersectionObserver/,
  // 客户端分享回调错误
  /clickShareIcon is not defined/,
  // vivo浏览器报错
  /window.bannerNight is not a function/,
  /Cannot set property 'insertStyle' of undefined/,
  /runCustomize is not defined/,
  /processRandomSelector is not defined/,
  // https://github.com/getsentry/sentry-javascript/issues/2074
  // https://github.com/getsentry/sentry-javascript/issues/3388
  /Cannot convert undefined or null to object/,
  /hardwareBackPress is not defined/,
  // 插件错误
  /Request aborted/,
  /'Event.path' is deprecated and will be removed in M109/,
  // swiper报错
  /Deprecation messages are stored in the devtools-frontend repo/,
  // UC浏览器
  /UCShellJava/,
  // 百度浏览器
  /window.android.unLoad is not a function/,
  // vite4加载失败
  /Importing a module script failed/,
  // 组件在加载前用户关闭页面并且刚好处于组件被解析的状态，比较卡时机
  /default is not defined/,
  // 浏览器触发 vivo浏览器19.8.0后的版本会有
  /window.changeNetWork is not a function/,
  /null is not an object \(evaluating '[a-z]\.(insertBefore|getAttribute|parentNode)'\)/,
  /Non-Error promise rejection captured with value: (close|cancel|confirm|undefined)/g,
];

// 移除自定义的打印样式和时间
const REG = /%c|([a-zA-Z-]+)\s*:[^;]+;|\s*\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/g
export default async (app, router, customErrHandler) => {
  if (IS_LOCAL) return app

  init({
    app,
    dsn: SENTRY_COMMONALITY_DSN,
    release: `${PROJECT}_${FOLDER}`,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: vueRouterInstrumentation(router),
      }),
      new Replay(),
    ],
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    beforeSend(event) {
      // exceptionData: 未捕获错误  extraData: 错误类型是引用类型才会存在(一般是业务接口错误结构)
      const exceptionData = event?.exception?.values?.[0]!;
      const extraData = event?.extra?.__serialized__;
      // console.log(exceptionData.value, extraData);
      // 只需要过滤业务上的错误,其他错误原样上报
      if (!exceptionData && !extraData) return event

      // 按业务报错原因设置标题
      if ((extraData as any)?.allDescription) {
        const { ret, desc, func } = (extraData as any).allDescription
        const funcName = (extraData as any)?.pbMap?.funcName || (extraData as any)?.funcName || func
        exceptionData.value = `[${funcName}] ${ret}: ${desc}`
      }

      (extraData as any)?.data && jsonStringify((extraData as any)?.data);
      (extraData as any)?.rpcOpts && jsonStringify((extraData as any)?.rpcOpts);


      if(exceptionData.value && exceptionData.value.indexOf('9999: invalid wire type 6 at offset 118') !== -1 && client.isApp) { // ios token问题定位 日志上传
        bridgeClient?.uploadLog?.({
          title: 'token异常上报',
          data: {
            origin: location.href,
          }
        })
      }

      if (
        (customErrHandler && customErrHandler(exceptionData.value!)) ||
        KEYWORD.some(regex => regex.test(exceptionData.value!))
      )
        return null

      // 整理上下文打印样式
      if (event?.breadcrumbs?.length) {
        event.breadcrumbs.forEach(item => {
          // 简要信息
          if (item?.message) item.message = item.message.replace(REG, "")
          // 详细信息
          if (item?.data?.arguments) {
            item.data.arguments = item.data.arguments.map((_item) => {
              if (isType(['String'], _item)) {
                _item = _item.replace(REG, "")
              }
              return _item
            }).filter(_item => _item)
          }
        })
      }

      // 离线包上报调整
      if (IS_APP_LOCAL && event?.request?.url) {
        event.request.url = `离线包-${ORIGIN_PATHNAME}`
      }

      return event
    },
  })
  return app
}
